import { useState, useEffect, useMemo } from 'react';
import { Loader2, AlertCircle, ChevronRight } from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { InspectionReportViewer } from './InspectionReportViewer';

interface HistoryReport {
  id: string;
  month: string; // 'YYYY-MM'
  created_at: string;
  is_draft: boolean | null;
  status_type: string | null;
  status_label: string | null;
  status_color: string | null;
  issue_count: number;
}

interface Props {
  jobSiteId: string;
  jobSiteName: string;
}

// The trailing 12 calendar months (oldest -> newest) as 'YYYY-MM'.
function trailingMonths(): string[] {
  const keys: string[] = [];
  const now = new Date();
  let y = now.getFullYear();
  let m = now.getMonth(); // 0-based
  for (let i = 0; i < 12; i++) {
    keys.unshift(`${y}-${String(m + 1).padStart(2, '0')}`);
    m -= 1;
    if (m < 0) { m = 11; y -= 1; }
  }
  return keys;
}

function monthLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'short' });
}

function fullMonthLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

export function JobSiteInspectionHistory({ jobSiteId, jobSiteName }: Props) {
  const [reports, setReports] = useState<HistoryReport[] | null>(null);
  const [error, setError] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [monthFilter, setMonthFilter] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setReports(null);
    setError(false);
    apiClient
      .get<{ reports: HistoryReport[] }>(`/api/job-sites/${jobSiteId}/inspection-history`)
      .then((r) => { if (active) setReports(r.reports ?? []); })
      .catch((err) => { console.error('Failed to load inspection history:', err); if (active) setError(true); })
      .finally(() => { /* state set above */ });
    return () => { active = false; };
  }, [jobSiteId]);

  const months = useMemo(() => trailingMonths(), []);
  const byMonth = useMemo(() => {
    const map = new Map<string, { issues: number; count: number }>();
    for (const key of months) map.set(key, { issues: 0, count: 0 });
    for (const r of reports ?? []) {
      const bucket = map.get(r.month);
      if (bucket) { bucket.issues += r.issue_count; bucket.count += 1; }
    }
    return map;
  }, [reports, months]);

  const maxIssues = useMemo(
    () => Math.max(1, ...months.map((k) => byMonth.get(k)?.issues ?? 0)),
    [byMonth, months],
  );

  if (selectedReportId) {
    return (
      <InspectionReportViewer reportId={selectedReportId} onBack={() => setSelectedReportId(null)} />
    );
  }

  if (reports === null && !error) {
    return (
      <div className="flex items-center justify-center py-16 text-theme-secondary">
        <Loader2 size={20} className="animate-spin mr-2" /> Loading inspection history…
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
        <AlertCircle size={16} /> Could not load inspection history for this property.
      </div>
    );
  }

  const all = reports ?? [];
  const visible = monthFilter ? all.filter((r) => r.month === monthFilter) : all;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-theme-primary">Inspection History</h3>
        <p className="text-sm text-theme-secondary">{jobSiteName} · {all.length} report{all.length === 1 ? '' : 's'}</p>
      </div>

      {/* Trailing 12-month histogram — bar height = issues found that month */}
      <div className="rounded-xl border border-line bg-surface p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold uppercase tracking-wide text-theme-secondary">Last 12 months — issues found</span>
          {monthFilter && (
            <button onClick={() => setMonthFilter(null)} className="text-xs text-blue-600 hover:underline">
              Clear filter ({fullMonthLabel(monthFilter)})
            </button>
          )}
        </div>
        <div className="flex items-end gap-1.5 h-32">
          {months.map((key) => {
            const b = byMonth.get(key) ?? { issues: 0, count: 0 };
            const heightPct = b.issues > 0 ? Math.max(6, (b.issues / maxIssues) * 100) : 0;
            const active = monthFilter === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setMonthFilter(active ? null : (b.count > 0 ? key : null))}
                disabled={b.count === 0}
                title={`${fullMonthLabel(key)}: ${b.count} report${b.count === 1 ? '' : 's'}, ${b.issues} issue${b.issues === 1 ? '' : 's'}`}
                className="flex-1 flex flex-col items-center justify-end h-full group disabled:cursor-default"
              >
                <span className="text-[10px] font-semibold text-theme-secondary mb-0.5">{b.issues > 0 ? b.issues : ''}</span>
                <div className="w-full flex items-end justify-center" style={{ height: '100%' }}>
                  {b.count > 0 ? (
                    <div
                      className={`w-full rounded-t transition-colors ${
                        active ? 'bg-blue-600' : b.issues > 0 ? 'bg-amber-400 group-hover:bg-amber-500' : 'bg-gray-200 group-hover:bg-gray-300' // theme-allow: histogram data bars (zero-issue state)
                      }`}
                      style={{ height: b.issues > 0 ? `${heightPct}%` : '6px' }}
                    />
                  ) : (
                    <div className="w-full h-px bg-subtle" />
                  )}
                </div>
                <span className="text-[10px] text-theme-secondary mt-1">{monthLabel(key)}</span>
                {b.count > 0 && (
                  <span className="text-[9px] text-theme-secondary leading-none">{b.count}×</span>
                )}
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-[11px] text-theme-secondary">Bar height = issues found; the small number below each bar is how many inspections were done that month. Tap a month to filter the list.</p>
      </div>

      {/* Reports list */}
      {visible.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line px-6 py-10 text-center text-sm text-theme-secondary">
          {all.length === 0 ? 'No inspection reports for this property yet.' : 'No reports in the selected month.'}
        </div>
      ) : (
        <div className="rounded-xl border border-line bg-surface divide-y divide-line">
          {visible.map((r) => (
            <button
              key={r.id}
              onClick={() => setSelectedReportId(r.id)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-subtle transition-colors"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-theme-primary">{fullMonthLabel(r.month)}</span>
                  {r.status_label && (
                    <span
                      className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded"
                      style={{ color: r.status_color ?? 'var(--color-text-primary)', backgroundColor: `${r.status_color ?? '#9CA3AF'}1A` }}
                    >
                      {r.status_label}
                    </span>
                  )}
                </div>
                <div className="text-xs text-theme-secondary mt-0.5">
                  Created {new Date(r.created_at).toLocaleDateString()}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span
                  className={`text-xs font-semibold px-2 py-1 rounded-full ${
                    r.issue_count > 0 ? 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300' : 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300'
                  }`}
                >
                  {r.issue_count} issue{r.issue_count === 1 ? '' : 's'}
                </span>
                <ChevronRight size={16} className="text-theme-secondary" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
